import React, {
  Fragment,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'
import { Link } from 'react-router-dom'

import { fees as feeUtils } from '@blocktickets/shared-utils'

import {
  createBankAccount,
  createListing,
  getBankAccount,
  getResaleFeeRules,
  updateMyListings,
} from '../../../utilities/api'
import { bankAccountSetupError } from '../../../utilities/bankAccountSetupError'
import {
  toggleElement,
  formatCurrency,
  getSinglarOrPluralWord,
  isPlural,
} from '../../../utilities/helpers'
import { useWindowSize } from '../../../utilities/hooks'

import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import BootstrapSpinner from 'react-bootstrap/Spinner'

import { BackButton } from '../../BackButton'
import { DisplayTickets } from '../DisplayTickets'
import { Numpad } from './Numpad'
import { SuccessContainer } from '../SuccessContainer'
import PayoutBusinessType, {
  isPayoutBusinessType,
} from '../../SettingsWrapper/PayoutBusinessType'

import { SuccessDisclaimer } from '../SuccessDisclaimer'
import { Spinner } from '../../LoadingContainer/Spinner'

export default function SellModal({
  handleClose,
  setTicketStatus,
  ticketAction,
  order,
  listing,
  getListings,
  getMyOrders,
  event,
}) {
  const windowSize = useWindowSize()
  const saleEvent = event || order?.event || listing?.event

  // 1 - sell
  // 2 - price
  // 3 - summary
  // 4 - success
  const [step, setStep] = useState(ticketAction === 'edit' ? 2 : 1)

  const [isUpdate, setIsUpdate] = useState(false)

  const [label, setLabel] = useState('Price per ticket')

  const [initialAskingPrice, setInitialAskingPrice] = useState()

  const [price, setPrice] = useState(0)

  const [priceValid, setPriceValid] = useState(true)

  const [serviceFees, setServiceFees] = useState(0)

  // selected tickets
  const [selectedTickets, setSelectedTickets] = useState([])

  const [isSaving, setIsSaving] = useState(false)

  const [isDisabled, setIsDisabled] = useState(false)

  const [payoutReady, setPayoutReady] = useState(
    ticketAction === 'edit' ? true : null
  )

  const [isLinkingPayout, setIsLinkingPayout] = useState(false)

  const [payoutError, setPayoutError] = useState('')

  const [businessType, setBusinessType] = useState('individual')

  const [existingBusinessType, setExistingBusinessType] = useState(null)

  const [payoutLookup, setPayoutLookup] = useState(
    ticketAction === 'edit' ? 'ready' : 'loading'
  )

  const payoutLookupRequest = useRef(0)

  const [resaleFeeRules, setResaleFeeRules] = useState(null)

  const ticketsTotalPrice = selectedTickets
    .map((ticket) => ticket.cost)
    .reduce((a, v) => a + v, 0)
  const ticketPrice = ticketsTotalPrice / selectedTickets.length

  const refreshPayoutAccount = () => {
    if (ticketAction === 'edit') return Promise.resolve(true)

    const requestId = payoutLookupRequest.current + 1
    payoutLookupRequest.current = requestId
    setPayoutLookup('loading')
    setPayoutError('')

    return getBankAccount()
      .then((res) => {
        if (requestId !== payoutLookupRequest.current) return false
        const ready = res?.data?.payouts_enabled === true
        const linkedType = res?.data?.business_type
        if (isPayoutBusinessType(linkedType)) {
          setBusinessType(linkedType)
          setExistingBusinessType(linkedType)
        } else {
          setExistingBusinessType(null)
        }
        setPayoutReady(ready)
        setPayoutLookup('ready')
        return ready
      })
      .catch(() => {
        if (requestId !== payoutLookupRequest.current) return false
        setPayoutReady(false)
        setPayoutLookup('error')
        setPayoutError(
          'Unable to check your payout account. Try again before linking a bank account.'
        )
        return false
      })
  }

  useEffect(() => {
    refreshPayoutAccount()
  }, [ticketAction])

  useEffect(() => {
    const eventId = saleEvent?.id
    if (!eventId) {
      setResaleFeeRules([])
      return
    }
    let active = true
    getResaleFeeRules(eventId)
      .then((res) => {
        if (active) setResaleFeeRules(res?.data?.rules || [])
      })
      .catch(() => {
        if (active) setResaleFeeRules([])
      })
    return () => {
      active = false
    }
  }, [saleEvent])

  useEffect(() => {}, [selectedTickets])

  useEffect(() => {
    const amountOfPrice =
      parseFloat(ticketPrice) *
      (Number(saleEvent?.resaleMinimumPercentage) / 100)
    const minResalePrice = ticketPrice + amountOfPrice
    if (
      saleEvent?.resaleEnableMinimum &&
      price > 0 &&
      minResalePrice &&
      price < minResalePrice
    ) {
      setLabel(`Pice must be at least ${formatCurrency(minResalePrice)}`)
      setPriceValid(false)
    } else {
      setLabel('Price per ticket')
      setPriceValid(true)
    }

    if (resaleFeeRules == null) return
    const askingPrice = Number(price) || 0
    const split = feeUtils.calculateResaleSellerFee(
      askingPrice,
      resaleFeeRules || []
    )
    const legacyRate =
      Number(saleEvent?.fee_structure?.secondaryServiceFeeSeller || 0) / 100
    const perTicket = split.hasRules
      ? split.sellerServiceFee
      : Math.round(askingPrice * legacyRate * 100) / 100
    setServiceFees(perTicket.toFixed(2))
  }, [price, resaleFeeRules, saleEvent, ticketPrice])

  useLayoutEffect(() => {
    let el = document.querySelector('.btn-close')

    if (el) {
      if (step === 4) toggleElement(el, false)

      return () => {
        toggleElement(el, true)
      }
    }
  }, [step])

  useEffect(() => {
    // set ticket price
    if (ticketAction === 'edit') {
      setIsUpdate(true)
      setSelectedTickets(listing.tickets)
      setInitialAskingPrice(listing.askingPrice)
      setPrice(listing.askingPrice)
    }
  }, [])

  useEffect(() => {
    // on edit
    if (ticketAction === 'edit')
      setIsDisabled(parseFloat(price) === initialAskingPrice)
  }, [initialAskingPrice, price])

  const linkPayoutAccount = () => {
    // The individual default is only a guess until the account lookup resolves.
    // Sending it early makes the API delete an unfinished organization account.
    if (payoutLookup !== 'ready') return

    setIsLinkingPayout(true)
    setPayoutError('')
    const payload = { businessType }
    if (isPayoutBusinessType(existingBusinessType)) {
      payload.currentBusinessType = existingBusinessType
    }
    createBankAccount(payload)
      .then((res) => {
        if (res?.data?.ready) {
          setPayoutReady(true)
          setIsLinkingPayout(false)
          return
        }
        if (res?.data?.link) {
          window.location.assign(res.data.link)
          return
        }
        setPayoutError('Unable to open bank account setup. Try again.')
        setIsLinkingPayout(false)
      })
      .catch((err) => {
        setPayoutError(bankAccountSetupError(err))
        setIsLinkingPayout(false)
      })
  }

  const handleGoBack = () => {
    setStep(step - 1)
  }

  const handleClick = () => {
    handleClose()
    if (!isUpdate && step === 4) setTicketStatus('listed')
  }

  const submit = () => {
    setIsSaving(true)
    let data = {
      tickets: selectedTickets,
      quantity: selectedTickets.length,
      askingPrice: parseFloat(price),
      event: saleEvent,
      serviceFees: parseFloat(serviceFees * selectedTickets?.length).toFixed(2),
      payout:
        parseFloat(price).toFixed(2) * selectedTickets?.length -
        parseFloat(serviceFees * selectedTickets.length).toFixed(2),
      fromOrder: order?.id,
      type: 'GA',
    }

    const matchingTicket = selectedTickets[0]
    if (matchingTicket) {
      const offerId =
        matchingTicket.offer?.id || matchingTicket.associatedOfferId
      const offers = saleEvent?.offers || order?.details?.event?.offers || []
      const offer =
        offers.find((item) => item.id === offerId) ||
        (offerId ? { id: offerId } : null)
      if (offer) data.offer = offer

      const isSeated = Boolean(
        matchingTicket.rowId &&
        matchingTicket.sectionId &&
        !matchingTicket.generalAdmission
      )
      if (isSeated) {
        data.rowId = matchingTicket.rowId
        data.sectionId = matchingTicket.sectionId
        data.sectionNumber = matchingTicket.sectionNumber
        data.rowNumber = matchingTicket.rowNumber
        data.type = 'SEATED'
      }
    }

    if (ticketAction === 'edit') {
      data.event = listing.event
      updateMyListings(listing.id, data)
        .then((res) => {
          setIsSaving(false)
          getListings().then(() => {
            setIsSaving(false)

            if (!isSaving) setStep(4)
          })
        })
        .catch((err) => {
          console.error(err)
          setIsSaving(false)
        })
    } else {
      createListing(data)
        .then((res) => {
          getMyOrders().then(() => {
            setIsSaving(false)

            if (!isSaving) setStep(4)
          })
        })
        .catch((err) => {
          setIsSaving(false)
          const message =
            err?.response?.data?.error?.message ||
            'Unable to list these tickets.'
          setPayoutError(message)
          if (message.toLowerCase().includes('bank account')) {
            setPayoutReady(false)
          }
          console.error(err)
        })
    }
  }

  const shouldShow = () => {
    if (!isUpdate) {
      // sell ticket
      return order?.tickets?.length > 0
    } else {
      // update ticket price
      return true
    }
  }

  return (
    <Fragment>
      <Modal.Header closeButton>
        <Modal.Title as="h5"> {isUpdate ? 'Edit' : 'Sell'}</Modal.Title>
      </Modal.Header>
      {payoutReady === null && (
        <Modal.Body>
          <Spinner />
        </Modal.Body>
      )}

      {payoutReady === false && (
        <>
          <Modal.Body>
            <div className="modal-body-heading">
              <h4 className="modal-body-heading-title modal-body-heading-title--sm mb-2">
                Link a bank account
              </h4>
              <p className="small text-muted fw-medium">
                A payout account is required before you can list tickets. After
                the event, your payout is sent to that account.
              </p>
              {payoutLookup !== 'error' && (
                <PayoutBusinessType
                  id="sell-payout-business-type"
                  value={businessType}
                  onChange={setBusinessType}
                  currentBusinessType={existingBusinessType}
                  disabled={isLinkingPayout || payoutLookup !== 'ready'}
                />
              )}
              {payoutError && (
                <p className="small text-danger mt-3 mb-0">{payoutError}</p>
              )}
            </div>
          </Modal.Body>
          <Stack direction="horizontal" className="btn-group-flex">
            <Button
              onClick={linkPayoutAccount}
              disabled={isLinkingPayout || payoutLookup !== 'ready'}
              className={`icon-button btn-h ${isLinkingPayout ? '' : 'btn-next'}`}
              size="lg"
            >
              {isLinkingPayout ? (
                <BootstrapSpinner
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                'Link bank account'
              )}
            </Button>
            <Button
              onClick={refreshPayoutAccount}
              variant="outline-light"
              size="lg"
              disabled={payoutLookup === 'loading'}
            >
              {payoutLookup === 'error' ? 'Try again' : "I've finished linking"}
            </Button>
          </Stack>
        </>
      )}

      {payoutReady && step === 1 && (
        <>
          <Modal.Body>
            <DisplayTickets
              status="sell"
              role="select"
              setSelectedTickets={setSelectedTickets}
              tickets={order?.tickets}
            />
          </Modal.Body>
          <Stack direction="horizontal" className="btn-group-flex">
            <Button
              onClick={() => setStep(2)}
              disabled={selectedTickets.length === 0}
              className="btn-next"
              size="lg"
            >
              Set price
            </Button>
          </Stack>
        </>
      )}

      {payoutReady && step === 2 && (
        <>
          <Modal.Body>
            <div className="modal-body-heading">
              <h4 className="modal-body-heading-title modal-body-heading-title--sm mb-2">
                Price your{' '}
                {getSinglarOrPluralWord(selectedTickets?.length).toLowerCase()}
              </h4>
              <p className="small text-muted fw-medium">
                Ticket face value {formatCurrency(ticketPrice)}
              </p>
            </div>
            <Form.Group
              controlId="price"
              className="form-card form-card-lg bg-info"
            >
              <Form.Label className={!priceValid ? 'text-danger' : ''}>
                {label}
              </Form.Label>
              <Form.Control
                readOnly={windowSize < 768}
                type="text"
                value={`$${price}`}
                maxLength="7"
                onChange={(e) => setPrice(e.target.value.substring(1).trim())}
                required
                autoFocus
              />
            </Form.Group>
            {windowSize < 768 && <Numpad price={price} setPrice={setPrice} />}
          </Modal.Body>
          <Stack direction="horizontal" className="btn-group-flex">
            {!isUpdate && (
              <BackButton variant="default" handleGoBack={handleGoBack} />
            )}
            <Button
              onClick={() => setStep(3)}
              className="btn-next"
              disabled={price === 0 || !price || !priceValid || isDisabled}
              size="lg"
            >
              {!isUpdate ? 'Payout summary' : 'Update price'}
            </Button>
          </Stack>
        </>
      )}
      {payoutReady && step === 3 && (
        <>
          <Modal.Body>
            <div className="modal-body-heading--with-border">
              <h4 className="modal-body-heading-title modal-body-heading-title--sm mb-2">
                Payment Summary
              </h4>
              <p>
                Please go to 'Settings' in your 'Wallet' and make sure your bank
                account is linked in order to receive funds from ticket sales.
              </p>
            </div>

            <div>
              <h2 className="normal text-uppercase text-muted mb-3">
                Ticket Breakdown
              </h2>
              <ul>
                <li className="list">
                  <p className="heading">Tickets</p>
                  <ul>
                    <Stack as="li" direction="horizontal" className="split-row">
                      <span>
                        Tickets: {formatCurrency(price)} x{' '}
                        {selectedTickets?.length}
                      </span>
                      <span className="text-end">
                        {formatCurrency(price * selectedTickets?.length)}
                      </span>
                    </Stack>
                  </ul>
                </li>
                <li className="list">
                  <p className="heading">Service Fees</p>
                  <ul>
                    <Stack as="li" direction="horizontal" className="split-row">
                      {' '}
                      <span>
                        Service Fees: {formatCurrency(serviceFees)} x{' '}
                        {selectedTickets?.length}
                      </span>
                      <span className="text-end">
                        (-
                        {formatCurrency(serviceFees * selectedTickets?.length)})
                      </span>
                    </Stack>
                  </ul>
                </li>
                <Stack
                  direction="horizontal"
                  as="li"
                  className="split-row list"
                >
                  <span className="heading m-0">Your Payout</span>
                  <span className="text-end fw-medium">
                    {formatCurrency(
                      price * selectedTickets?.length -
                        parseFloat(serviceFees * selectedTickets?.length)
                    )}
                  </span>
                </Stack>
              </ul>
            </div>
            <small className="disclaimer mt-4 mb-0">
              By clicking 'Agree and sell' you are constenting to Blocktickets{' '}
              <a
                href="https://blocktickets.xyz/terms-conditions"
                target="_blank"
              >
                terms and conditions
              </a>
              .{' '}
            </small>
            {payoutError && (
              <p className="small text-danger mt-3 mb-0">{payoutError}</p>
            )}
          </Modal.Body>
          <Stack direction="horizontal" className="btn-group-flex">
            <BackButton variant="default" handleGoBack={handleGoBack} />
            <Button
              onClick={submit}
              className={`icon-button btn-h
                                ${!isSaving ? 'btn-next' : ''}`}
              size="lg"
              disabled={isSaving}
            >
              {isSaving ? <Spinner /> : 'Agree and sell'}
            </Button>
          </Stack>
        </>
      )}
      {payoutReady && step === 4 && (
        <>
          <Modal.Body>
            <SuccessContainer>
              <h4 className="modal-body-heading-title">
                {isUpdate
                  ? 'Your ticket price has been updated'
                  : `Your ${getSinglarOrPluralWord(selectedTickets?.length).toLowerCase()} ${isPlural(selectedTickets.length) ? 'are' : 'is'} listed for sale!`}
              </h4>
            </SuccessContainer>
            <p className="small">
              {isUpdate
                ? `Your updated price will be in effect within 2 hours on the marketplace. If your ${getSinglarOrPluralWord(selectedTickets?.length).toLowerCase()} ${isPlural(selectedTickets.length) ? 'are' : 'is'} sold before the price is updated you will receive funds based on the original price.`
                : `We will notify you via sms if a purchase is made. While your ${getSinglarOrPluralWord(selectedTickets?.length).toLowerCase()} ${isPlural(selectedTickets.length) ? 'are' : 'is'} listed, you can change the price or delist them from the marketplace in 'My listings' at anytime`}
            </p>
            <SuccessDisclaimer />
          </Modal.Body>
          <Stack className="btn-group-flex">
            {!isUpdate && (
              <Link to="/my-listings" className="btn btn-lg btn-outline-light">
                Go to My listings
              </Link>
            )}
            {shouldShow() && (
              <Button onClick={handleClick} size="lg">
                Close
              </Button>
            )}
          </Stack>
        </>
      )}
    </Fragment>
  )
}
